import { PayloadAction } from '@reduxjs/toolkit'
import { IActionInstitutionsDescribe, IActionInstitutionsRequest, InstitutionsTypes } from './types'
import { all, apply, put, takeLatest } from 'redux-saga/effects'
import { InstitutionsActions } from './index'
import institutionsService from '../../services/intitutions.service'
import { SnackbarActions, SnackBarMessageType } from '../snackbar'
import Messags from '../../assets/locales/pt/translation.json'
import Messages from '../../assets/locales/pt/translation.json'

/**
 * <h5>Generator functions that make calls to the authentication service to obtain the data.</h5>
 * @see {@link https://developer.mozilla.org/pt-BR/docs/Web/JavaScript/Reference/Statements/function*}
 * @typedef InstitutionsSagas
 * @namespace InstitutionsSagas
 * @category React
 * @subcategory Redux / Sagas
 */

/**
 * Generating function that calls the login service to authenticate the system.
 * @memberof InstitutionsSagas
 * @alias InstitutionsSagas.request
 * @function
 * @category React
 * @subcategory Redux / Sagas
 * @param {PayloadAction<IActionInstitutionsRequest>} action User triggered action data.
 */
export function* request(action: PayloadAction<IActionInstitutionsRequest>) {
    const { institutionsSuccess, institutionsFailure } = InstitutionsActions
    const { openSnackBar } = SnackbarActions
    try {
        let filters = action.payload
        if (filters.municipality) {
            const { uf, ...filtersNoUf } = filters
            filters = filtersNoUf
        }
        const data = yield apply(institutionsService, institutionsService.institution, [filters])
        yield put(institutionsSuccess({ data }))
    } catch (err: any) {
        yield put(institutionsFailure())
        yield put(openSnackBar({
            title: 'Erro Interno.',
            type: SnackBarMessageType.ERROR,
            message: `${Messags.REQUEST.INSTITUTIONS.REQUEST.INTERNAL}`
        }))
    }
}

/**
 * Generating function that calls the login service to authenticate the system.
 * @memberof InstitutionsSagas
 * @alias InstitutionsSagas.describe
 * @function
 * @category React
 * @subcategory Redux / Sagas
 * @param {PayloadAction<IActionInstitutionsDescribe>} action User triggered action data.
 */
export function* describe(action: PayloadAction<IActionInstitutionsRequest>) {
    const {
        describeSuccess,
        describeFailure,
        empsRequest,
        notesRequest,
        biddingsRequest,
        contractsRequest,
        additivesRequest,
        paymentsRequest,
        payrollrecordRequest
    } = InstitutionsActions
    const { openSnackBar } = SnackbarActions
    try {
        const filters = action.payload
        const institution = yield apply(institutionsService, institutionsService.institution, [filters])
        // Data de hoje
        const endDate = new Date()
        // Subtrair 6 meses
        const startDate = new Date()
        startDate.setMonth(startDate.getMonth() - 2)

        // Função para formatar a data no formato yyyy-mm-dd
        const formatDate = (date: Date) => {
            return date.toISOString().split('T')[0]
        }

        // @ts-ignore
        yield put(empsRequest({
            cnpj: institution[0]?.cnpj,
            start_date: formatDate(startDate),
            end_date: formatDate(endDate)
        }))

        // @ts-ignore
        yield put(notesRequest({
            cnpj: institution[0]?.cnpj,
            start_date: formatDate(startDate),
            end_date: formatDate(endDate),
            limitation: false
        }))

        // @ts-ignore
        yield put(biddingsRequest({
            cnpj: institution[0]?.cnpj,
            start_date: formatDate(startDate),
            end_date: formatDate(endDate)
        }))

        // @ts-ignore
        yield put(contractsRequest({
            cnpj: institution[0]?.cnpj,
            start_date: formatDate(startDate),
            end_date: formatDate(endDate)
        }))

        // @ts-ignore
        yield put(additivesRequest({
            cnpj: institution[0]?.cnpj,
            start_date: formatDate(startDate),
            end_date: formatDate(endDate)
        }))

        // @ts-ignore
        yield put(paymentsRequest({
            cnpj: institution[0]?.cnpj,
            start_date: formatDate(startDate),
            end_date: formatDate(endDate)
        }))

        // @ts-ignore
        yield put(payrollrecordRequest({
            cnpj: institution[0]?.cnpj,
            start_date: formatDate(startDate),
            end_date: formatDate(endDate)
        }))

        yield put(describeSuccess({
            data: {
                org: institution[0]?.name,
                cnpj: institution[0]?.cnpj
            }
        }))

        yield put(openSnackBar({
            type: SnackBarMessageType.INFO,
            title: 'Resposta em intervalo.',
            message: 'O resultado das pesquisas são referentes aos ultimos 90 dias!'
        }))
    } catch (err: any) {
        yield put(describeFailure())
        if (err.response.status >= 400 && err.response.status <= 499) {
            yield put(openSnackBar({
                title: 'Registro não encontrado.',
                type: SnackBarMessageType.WARNING,
                message: `${Messags.REQUEST.INSTITUTIONS.DESCRIBE.INVALID}`
            }))
        } if (err.response.status >= 500 && err.response.status <= 599) {
            yield put(openSnackBar({
                title: 'Erro Interno.',
                type: SnackBarMessageType.ERROR,
                message: `${Messags.REQUEST.INSTITUTIONS.REQUEST.INTERNAL}`
            }))
        }
    }
}


/**
 * Generating function that calls the login service to authenticate the system.
 * @memberof InstitutionsSagas
 * @alias InstitutionsSagas.emps
 * @function
 * @category React
 * @subcategory Redux / Sagas
 * @param {PayloadAction<IActionInstitutionsDescribe>} action User triggered action data.
 */
export function* emps(action: PayloadAction<IActionInstitutionsDescribe>) {
    const { empsSuccess, empsFailure } = InstitutionsActions
    try {
        const { paginator, ...filters } = action.payload
        // @ts-ignore
        const emps = yield apply(institutionsService, institutionsService.getEmps, [filters, paginator])
        yield put(empsSuccess({
            data: {
                dataEmps: {
                    data: emps.data,
                    total: emps.total
                }
            }
        }))
    } catch (err: any) {
        yield put(empsFailure())
    }
}

export function* notes(action: PayloadAction<IActionInstitutionsDescribe>) {
    const { notesRequestSuccess, notesRequestFailure } = InstitutionsActions
    const { openSnackBar } = SnackbarActions
    try {
        const { limitation, ...filters } = action.payload
        const notes = yield apply(institutionsService, institutionsService.getNotes, [filters])
        yield put(notesRequestSuccess({
            data: notes
        }))

    } catch (err: any) {
        yield put(notesRequestFailure())
        if (err.response.status >= 500 && err.response.status <= 599) {
            yield put(openSnackBar({
                title: 'Erro Interno.',
                type: SnackBarMessageType.ERROR,
                message: `${Messages.REQUEST.PERSON.REQUEST.INTERNAL}`
            }))
        }
    }
}

export function* products(action: PayloadAction<IActionInstitutionsDescribe>) {
    const { itensNotesSuccess, itensNotesFailure } = InstitutionsActions
    const { openSnackBar } = SnackbarActions
    try {
        const { access_key, recipient_document } = action.payload
        // @ts-ignore
        const itens = yield apply(institutionsService, institutionsService.getProducts, [access_key, recipient_document])
        yield put(itensNotesSuccess({
            data: itens
        }))
    } catch (err: any) {
        yield put(itensNotesFailure())
        if (err.response.status >= 500 && err.response.status <= 599) {
            yield put(openSnackBar({
                title: 'Erro Interno.',
                type: SnackBarMessageType.ERROR,
                message: `${Messages.REQUEST.PERSON.REQUEST.INTERNAL}`
            }))
        }
    }
}

export function* biddings(action: PayloadAction<IActionInstitutionsDescribe>) {
    const { biddingsSuccess, biddingsFailure } = InstitutionsActions
    const { openSnackBar } = SnackbarActions
    try {
        const { paginator, ...filters } = action.payload
        // @ts-ignore
        const response = yield apply(institutionsService, institutionsService.getBiddings, [filters, paginator])
        yield put(biddingsSuccess({ data: response.data }))
    } catch (err: any) {
        yield put(biddingsFailure())
        yield put(openSnackBar({
            title: 'Erro Interno.',
            type: SnackBarMessageType.ERROR,
            message: `${Messages.REQUEST.PERSON.REQUEST.INTERNAL}`
        }))
    }
}

export function* contracts(action: PayloadAction<IActionInstitutionsDescribe>) {
    const { contractsSuccess, contractsFailure } = InstitutionsActions
    const { openSnackBar } = SnackbarActions
    try {
        const { paginator, ...filters } = action.payload
        // @ts-ignore
        const response = yield apply(institutionsService, institutionsService.getContracts, [filters, paginator])
        yield put(contractsSuccess({ data: response.data }))
    } catch (err: any) {
        yield put(contractsFailure())
        yield put(openSnackBar({
            title: 'Erro Interno.',
            type: SnackBarMessageType.ERROR,
            message: `${Messages.REQUEST.PERSON.REQUEST.INTERNAL}`
        }))
    }
}

export function* additives(action: PayloadAction<IActionInstitutionsDescribe>) {
    const { additivesSuccess, additivesFailure } = InstitutionsActions
    const { openSnackBar } = SnackbarActions
    try {
        const { paginator, ...filters } = action.payload
        // @ts-ignore
        const response = yield apply(institutionsService, institutionsService.getAdditives, [filters, paginator])
        yield put(additivesSuccess({ data: response.data }))
    } catch (err: any) {
        yield put(additivesFailure())
        yield put(openSnackBar({
            title: 'Erro Interno.',
            type: SnackBarMessageType.ERROR,
            message: `${Messages.REQUEST.PERSON.REQUEST.INTERNAL}`
        }))
    }
}

export function* payments(action: PayloadAction<IActionInstitutionsDescribe>) {
    const { paymentsSuccess, paymentsFailure } = InstitutionsActions
    const { openSnackBar } = SnackbarActions
    try {
        const { paginator, ...filters } = action.payload
        // @ts-ignore
        const response = yield apply(institutionsService, institutionsService.getPayments, [filters, paginator])
        yield put(paymentsSuccess({ data: response.data }))
    } catch (err: any) {
        yield put(paymentsFailure())
        yield put(openSnackBar({
            title: 'Erro Interno.',
            type: SnackBarMessageType.ERROR,
            message: `${Messages.REQUEST.PERSON.REQUEST.INTERNAL}`
        }))
    }
}
export function* payrollRecords(action: PayloadAction<IActionInstitutionsDescribe>) {
    const { payrollrecordFailure, payrollrecordSuccess } = InstitutionsActions
    const { openSnackBar } = SnackbarActions
    try {
        const { paginator, ...filters } = action.payload
        // @ts-ignore
        const response = yield apply(institutionsService, institutionsService.getPayrollRecords, [filters, paginator])
        yield put(payrollrecordSuccess({ data: response.data }))
    } catch (err: any) {
        yield put(payrollrecordFailure())
        yield put(openSnackBar({
            title: 'Erro Interno.',
            type: SnackBarMessageType.ERROR,
            message: `${Messages.REQUEST.PERSON.REQUEST.INTERNAL}`
        }))
    }
}


const institutionsSaga = function* () {
    yield all([
        takeLatest(InstitutionsTypes.INSTITUTIONS_REQUEST, request),
        takeLatest(InstitutionsTypes.DESCRIBE_REQUEST, describe),
        takeLatest(InstitutionsTypes.EMPS_REQUEST, emps),
        takeLatest(InstitutionsTypes.NOTES_REQUEST, notes),
        takeLatest(InstitutionsTypes.ITENS_NOTES_REQUEST, products),
        takeLatest(InstitutionsTypes.BIDDINGS_REQUEST, biddings),
        takeLatest(InstitutionsTypes.CONTRACTS_REQUEST, contracts),
        takeLatest(InstitutionsTypes.ADDITIVES_REQUEST, additives),
        takeLatest(InstitutionsTypes.PAYMENTS_REQUEST, payments),
        takeLatest(InstitutionsTypes.PAYROLLRECORD_REQUEST, payrollRecords),
    ])
}

export default institutionsSaga
